import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, IconButton, Chip, CircularProgress, TextField, InputAdornment, Switch } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import SearchIcon from "@material-ui/icons/Search";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    flex: 1, width: "100%", height: "100%",
    display: "flex", flexDirection: "column", overflow: "hidden",
    background: theme.palette.type === "dark"
      ? "linear-gradient(145deg, #060D1A 0%, #0A1628 100%)"
      : "linear-gradient(145deg, #EBF3FF 0%, #F0F6FF 100%)",
  },
  hero: {
    flexShrink: 0,
    margin: theme.spacing(3, 1.75, 0),
    padding: 0,
    background: "transparent",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    position: "relative",
    overflow: "visible",
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(2.25, 1.25, 0),
      padding: 0,
      alignItems: "flex-start",
      flexDirection: "column",
    },
  },
  heroLeft: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    zIndex: 1,
  },
  heroTitle: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 800,
    fontSize: "clamp(21px, 2vw, 27px)",
    lineHeight: 1.15,
    letterSpacing: "-0.02em",
    color: theme.palette.type === "dark" ? "#fff" : "#0A1628",
  },
  heroSub: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 400,
    fontSize: "12.5px",
    lineHeight: 1.5,
    color: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.58)"
      : "rgba(10,22,40,0.62)",
    marginTop: 4,
  },
  heroRight: { display: "flex", alignItems: "center", gap: theme.spacing(1.5), zIndex: 1 },
  addBtn: {
    borderRadius: 12, fontWeight: 700, fontSize: "0.82rem", textTransform: "none",
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    color: "#fff", padding: theme.spacing(0.9, 2.5), border: "none", cursor: "pointer",
    boxShadow: "0 4px 14px rgba(0,102,255,0.28)", display: "flex", alignItems: "center", gap: 6,
  },
  toolbar: {
    flexShrink: 0, display: "flex", alignItems: "center", gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2), flexWrap: "wrap",
  },
  searchField: {
    flex: 1, minWidth: 200,
    "& .MuiOutlinedInput-root": { borderRadius: 10, fontSize: "0.84rem",
      background: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#fff" },
  },
  filterChip: {
    borderRadius: 8, fontWeight: 600, fontSize: "0.75rem", cursor: "pointer",
    transition: "all 0.15s",
  },
  grid: {
    flex: 1, overflowY: "auto", padding: theme.spacing(0, 2, 2),
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(1.5), alignContent: "start",
    ...theme.scrollbarStyles,
  },
  card: {
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#fff",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "#e8edf5"}`,
    borderRadius: 16, padding: theme.spacing(2), display: "flex", flexDirection: "column", gap: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)", transition: "all 0.18s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,102,255,0.1)", borderColor: "rgba(0,102,255,0.2)" },
  },
  cardTop: { display: "flex", alignItems: "flex-start", gap: theme.spacing(1.2) },
  cardIconWrap: {
    width: 40, height: 40, borderRadius: 12, flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem",
  },
  cardTitle: { fontWeight: 700, fontSize: "0.9rem", color: theme.palette.text.primary, lineHeight: 1.3 },
  cardDesc: { fontSize: "0.75rem", color: theme.palette.text.secondary, lineHeight: 1.4,
    overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
    "-webkit-line-clamp": 2, "-webkit-box-orient": "vertical" },
  cardFooter: { display: "flex", alignItems: "center", gap: 6, paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`, flexWrap: "wrap" },
  cardActions: { display: "flex", gap: 4, marginLeft: "auto" },
  actionBtn: {
    width: 28, height: 28, borderRadius: 7, padding: 0, border: `1px solid ${theme.palette.divider}`,
    "&:hover": { background: "rgba(0,102,255,0.08)", borderColor: "rgba(0,102,255,0.3)" },
  },
  emptyState: {
    gridColumn: "1 / -1", display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", padding: theme.spacing(8), gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  // MODAL
  modalOverlay: {
    position: "fixed", inset: 0, zIndex: 1300, background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center",
    padding: 20,
  },
  modal: {
    background: theme.palette.type === "dark" ? "#0D1626" : "#fff",
    borderRadius: 20, width: "100%", maxWidth: 560, maxHeight: "90vh",
    overflow: "hidden", display: "flex", flexDirection: "column",
    boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
  },
  modalHeader: {
    padding: "16px 20px", background: "linear-gradient(135deg, #0066FF, #00A3FF)",
    display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
  },
  modalHeaderIcon: {
    width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.2)",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem",
  },
  modalBody: { padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14, ...theme.scrollbarStyles },
  modalFooter: {
    padding: "14px 20px", borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex", gap: 10, justifyContent: "flex-end", flexShrink: 0,
  },
  fieldLabel: { fontWeight: 700, fontSize: "0.8rem", color: theme.palette.text.primary, marginBottom: 5, display: "block" },
  typeGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 },
  typeCard: {
    border: "2px solid", borderRadius: 10, padding: "10px 8px", cursor: "pointer",
    textAlign: "center", transition: "all 0.15s",
  },
  uploadArea: {
    border: "2px dashed rgba(0,102,255,0.25)", borderRadius: 12, padding: "20px",
    textAlign: "center", cursor: "pointer", transition: "all 0.15s",
    "&:hover": { borderColor: "#0066FF", background: "rgba(0,102,255,0.03)" },
  },
  cancelBtn: {
    borderRadius: 10, fontWeight: 700, fontSize: "0.82rem", textTransform: "none",
    border: "1px solid rgba(0,102,255,0.2)", color: "#0066FF", padding: "8px 20px",
    background: "transparent", cursor: "pointer",
  },
  saveBtn: {
    borderRadius: 10, fontWeight: 700, fontSize: "0.82rem", textTransform: "none",
    background: "linear-gradient(135deg, #0066FF, #00A3FF)", color: "#fff",
    padding: "8px 24px", border: "none", cursor: "pointer",
    boxShadow: "0 4px 14px rgba(0,102,255,0.28)",
  },
}));

const TYPE_CONFIG = {
  image: { emoji: "🖼️", label: "Imagem", color: "#0066FF", bg: "rgba(0,102,255,0.1)" },
  video: { emoji: "🎥", label: "Vídeo", color: "#7C3AED", bg: "rgba(124,58,237,0.1)" },
  audio: { emoji: "🎵", label: "Áudio", color: "#00A86B", bg: "rgba(0,168,107,0.1)" },
  pdf:   { emoji: "📄", label: "PDF",   color: "#F59E0B", bg: "rgba(245,158,11,0.1)" },
};

const FILTERS = ["todos", "image", "video", "audio", "pdf"];
const FILTER_LABELS = { todos: "Todos", image: "Imagens", video: "Vídeos", audio: "Áudios", pdf: "PDFs" };

const initialForm = { name: "", type: "image", description: "", usageInstruction: "", triggerKeywords: "", agentId: "", isActive: true, mediaKey: "" };

const KoraxIALibrary = () => {
  const classes = useStyles();
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("todos");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const fileRef = useRef();

  const fetchMedias = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "todos") params.type = filter;
      if (search) params.search = search;
      const { data } = await api.get("/cavia-medias", { params });
      setMedias(data);
    } catch { toast.error("Erro ao carregar mídias"); }
    setLoading(false);
  };

  useEffect(() => { fetchMedias(); }, [filter, search]);
  useEffect(() => {
    api.get("/prompt").then(({ data }) => setPrompts(data.prompts || [])).catch(() => {});
  }, []);

  const openAdd = () => { setEditing(null); setForm(initialForm); setFile(null); setShowModal(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name, type: m.type, description: m.description || "", usageInstruction: m.usageInstruction || "", triggerKeywords: (m.triggerKeywords || []).join(", "), agentId: m.agentId || "", isActive: m.isActive, mediaKey: m.mediaKey || "" });
    setFile(null); setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.type) return toast.error("Nome e tipo são obrigatórios");
    if (!editing && !file) return toast.error("Selecione um arquivo");
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      fd.set("mediaKey", form.mediaKey.toLowerCase().replace(/[^a-z0-9_]/g, "_"));
      const keywords = form.triggerKeywords.split(",").map(s => s.trim()).filter(Boolean);
      fd.set("triggerKeywords", JSON.stringify(keywords));
      if (file) fd.append("file", file);
      if (editing) {
        await api.put(`/cavia-medias/${editing.id}`, { ...form, triggerKeywords: keywords });
      } else {
        await api.post("/cavia-medias", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(editing ? "Mídia atualizada!" : "Mídia adicionada!");
      setShowModal(false); fetchMedias();
    } catch { toast.error("Erro ao salvar mídia"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover esta mídia?")) return;
    try { await api.delete(`/cavia-medias/${id}`); toast.success("Removida!"); fetchMedias(); }
    catch { toast.error("Erro ao remover"); }
  };

  const handleToggle = async (id) => {
    try { await api.patch(`/cavia-medias/${id}/toggle`); fetchMedias(); }
    catch { toast.error("Erro ao alterar status"); }
  };

  const totalAtivos = medias.filter(m => m.isActive).length;

  return (
    <div className={classes.pageRoot}>
      {/* HERO */}
      <div className={classes.hero}>
        <div className={classes.heroLeft}>
          <div>
            <Typography className={classes.heroTitle}>Biblioteca da Korax IA</Typography>
            <Typography className={classes.heroSub}>Mídias comerciais que a IA pode enviar no momento certo</Typography>
          </div>
        </div>
        <div className={classes.heroRight}>
          <Chip size="small" label={`${totalAtivos} ativas`}
            style={{ background: "rgba(0,168,107,0.12)", color: "#00A86B", border: "1px solid rgba(0,168,107,0.2)", fontWeight: 700, fontSize: "0.72rem" }} />
          <button className={classes.addBtn} onClick={openAdd}>
            <AddIcon style={{ fontSize: 16 }} /> Adicionar mídia
          </button>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className={classes.toolbar}>
        <TextField size="small" variant="outlined" placeholder="Buscar mídia..."
          value={search} onChange={e => setSearch(e.target.value)}
          className={classes.searchField}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 18, color: "#0066FF" }} /></InputAdornment> }} />
        {FILTERS.map(f => (
          <Chip key={f} label={FILTER_LABELS[f]} size="small"
            className={classes.filterChip}
            onClick={() => setFilter(f)}
            style={{
              background: filter === f ? "#0066FF" : "rgba(0,102,255,0.06)",
              color: filter === f ? "#fff" : "#0066FF",
              border: `1px solid ${filter === f ? "#0066FF" : "rgba(0,102,255,0.2)"}`,
            }} />
        ))}
      </div>

      {/* GRID */}
      <div className={classes.grid}>
        {loading && [1,2,3,4,5,6].map(i => (
          <div key={i} className={classes.card} style={{ opacity: 0.4 }}>
            <div style={{ height: 12, borderRadius: 6, background: "#e2e8f0", width: "60%", marginBottom: 8 }} />
            <div style={{ height: 10, borderRadius: 6, background: "#e2e8f0", width: "80%" }} />
          </div>
        ))}

        {!loading && medias.length === 0 && (
          <div className={classes.emptyState}>
            <span style={{ fontSize: "3rem", opacity: 0.3 }}>📚</span>
            <Typography style={{ fontWeight: 700, fontSize: "0.9rem" }}>Nenhuma mídia encontrada</Typography>
            <Typography style={{ fontSize: "0.78rem" }}>Adicione sua primeira mídia clicando em "Adicionar mídia"</Typography>
          </div>
        )}

        {!loading && medias.map(m => {
          const tc = TYPE_CONFIG[m.type] || TYPE_CONFIG.image;
          return (
            <div key={m.id} className={classes.card} style={{ opacity: m.isActive ? 1 : 0.55 }}>
              <div className={classes.cardTop}>
                <div className={classes.cardIconWrap} style={{ background: tc.bg }}>
                  <span>{tc.emoji}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Typography className={classes.cardTitle}>{m.name}</Typography>
                  <Chip size="small" label={tc.label} style={{ height: 18, fontSize: "0.65rem", fontWeight: 700, background: tc.bg, color: tc.color, border: `1px solid ${tc.color}22`, marginTop: 3 }} />
                </div>
              </div>

              {m.mediaKey && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(124,58,237,0.08)", border: "1px solid rgba(124,58,237,0.2)", borderRadius: 6, padding: "2px 8px", marginTop: 2 }}>
                  <span style={{ fontSize: "0.65rem", fontWeight: 800, color: "#7C3AED", fontFamily: "monospace" }}>[ENVIAR_MIDIA: {m.mediaKey}]</span>
                </div>
              )}
              {m.description && <Typography className={classes.cardDesc}>{m.description}</Typography>}

              {m.usageInstruction && (
                <div style={{ background: "rgba(0,102,255,0.05)", border: "1px solid rgba(0,102,255,0.1)", borderRadius: 8, padding: "6px 10px" }}>
                  <Typography style={{ fontSize: "0.72rem", color: "#0066FF", fontWeight: 600 }}>💡 {m.usageInstruction}</Typography>
                </div>
              )}

              {m.triggerKeywords?.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {m.triggerKeywords.slice(0, 3).map((k, i) => (
                    <span key={i} style={{ fontSize: "0.65rem", fontWeight: 600, padding: "2px 7px", borderRadius: 6, background: "rgba(0,0,0,0.05)", color: "#64748b" }}>"{k}"</span>
                  ))}
                  {m.triggerKeywords.length > 3 && <span style={{ fontSize: "0.65rem", color: "#94a3b8" }}>+{m.triggerKeywords.length - 3}</span>}
                </div>
              )}

              <div className={classes.cardFooter}>
                <Chip size="small"
                  label={m.isActive ? "Ativa" : "Inativa"}
                  icon={m.isActive ? <CheckCircleIcon style={{ fontSize: 12 }} /> : undefined}
                  style={{ height: 20, fontSize: "0.65rem", fontWeight: 700,
                    background: m.isActive ? "rgba(0,168,107,0.1)" : "rgba(0,0,0,0.05)",
                    color: m.isActive ? "#00A86B" : "#94a3b8",
                    border: `1px solid ${m.isActive ? "rgba(0,168,107,0.2)" : "rgba(0,0,0,0.08)"}` }} />
                <div className={classes.cardActions}>
                  <Switch size="small" checked={m.isActive} onChange={() => handleToggle(m.id)}
                    style={{ color: m.isActive ? "#0066FF" : "#94a3b8" }} />
                  <IconButton size="small" className={classes.actionBtn} onClick={() => openEdit(m)}>
                    <EditIcon style={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton size="small" className={classes.actionBtn} onClick={() => handleDelete(m.id)}>
                    <DeleteIcon style={{ fontSize: 14, color: "#EF4444" }} />
                  </IconButton>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className={classes.modalOverlay} onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className={classes.modal}>
            <div className={classes.modalHeader}>
              <div className={classes.modalHeaderIcon}>📚</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: "0.95rem", color: "#fff" }}>{editing ? "Editar Mídia" : "Adicionar Mídia"}</div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)", marginTop: 1 }}>Configure como a Korax IA vai usar este material</div>
              </div>
              <IconButton size="small" style={{ color: "rgba(255,255,255,0.8)" }} onClick={() => setShowModal(false)}>
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            </div>

            <div className={classes.modalBody}>
              {/* Nome */}
              <div>
                <label className={classes.fieldLabel}>Nome da mídia *</label>
                <TextField fullWidth size="small" variant="outlined" placeholder="Ex: Vídeo demonstração CAVChat"
                  value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  style={{ "& .MuiOutlinedInput-root": { borderRadius: 10 } }} />
              </div>

              {/* Chave da mídia */}
              <div>
                <label className={classes.fieldLabel}>Chave da mídia (mediaKey) *</label>
                <TextField fullWidth size="small" variant="outlined"
                  placeholder="Ex: audio_boas_vindas, video_demo, imagem_produto"
                  value={form.mediaKey}
                  onChange={e => setForm(f => ({ ...f, mediaKey: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") }))} />
                <span style={{ fontSize: "0.7rem", color: "#94a3b8", marginTop: 4, display: "block" }}>
                  Use no prompt assim: <strong>[ENVIAR_MIDIA: {form.mediaKey || "sua_chave"}]</strong>
                </span>
              </div>

              {/* Tipo */}
              {!editing && (
                <div>
                  <label className={classes.fieldLabel}>Tipo de mídia *</label>
                  <div className={classes.typeGrid}>
                    {Object.entries(TYPE_CONFIG).map(([key, tc]) => (
                      <div key={key} className={classes.typeCard}
                        onClick={() => setForm(f => ({ ...f, type: key }))}
                        style={{ borderColor: form.type === key ? tc.color : "#e8edf5", background: form.type === key ? tc.bg : "transparent" }}>
                        <div style={{ fontSize: "1.4rem", marginBottom: 4 }}>{tc.emoji}</div>
                        <div style={{ fontSize: "0.72rem", fontWeight: 700, color: form.type === key ? tc.color : "#64748b" }}>{tc.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload */}
              {!editing && (
                <div>
                  <label className={classes.fieldLabel}>Arquivo *</label>
                  <div className={classes.uploadArea} onClick={() => fileRef.current?.click()}>
                    <input ref={fileRef} type="file" hidden
                      accept={form.type === "image" ? "image/*" : form.type === "video" ? "video/*" : form.type === "audio" ? "audio/*" : ".pdf"}
                      onChange={e => setFile(e.target.files[0])} />
                    {file ? (
                      <div>
                        <div style={{ fontSize: "1.5rem", marginBottom: 4 }}>✅</div>
                        <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "#00A86B" }}>{file.name}</div>
                        <div style={{ fontSize: "0.7rem", color: "#94a3b8", marginTop: 2 }}>{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                      </div>
                    ) : (
                      <div>
                        <CloudUploadIcon style={{ fontSize: 32, color: "#0066FF", opacity: 0.5, marginBottom: 4 }} />
                        <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#0066FF" }}>Clique para selecionar</div>
                        <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: 2 }}>
                          {form.type === "audio" ? "⚠️ Áudio será enviado como voice note/PTT" : "Arraste ou clique para fazer upload"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Legenda */}
              <div>
                <label className={classes.fieldLabel}>Legenda (aparece embaixo da imagem/vídeo)</label>
                <TextField fullWidth size="small" variant="outlined" multiline rows={2}
                  placeholder="Ex: Par de Alianças 6mm Diamantada — R$ 289,90 💍"
                  value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                <span style={{ fontSize: "0.7rem", color: "#94a3b8", marginTop: 4, display: "block" }}>A IA consegue ler esta legenda para identificar o produto quando o cliente responder.</span>
              </div>


              {/* Status */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "rgba(0,102,255,0.04)", borderRadius: 10, border: "1px solid rgba(0,102,255,0.08)" }}>
                <Switch size="small" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.82rem" }}>Mídia ativa</div>
                  <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>A Korax IA só usa mídias ativas</div>
                </div>
              </div>
            </div>

            <div className={classes.modalFooter}>
              <button className={classes.cancelBtn} onClick={() => setShowModal(false)}>Cancelar</button>
              <button className={classes.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={14} color="inherit" /> : (editing ? "Salvar alterações" : "Adicionar mídia")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KoraxIALibrary;
